<template>
  <div class="w-full">
    <CatalogCombobox
      v-if="!isOtherMode"
      label="Especialidade"
      :display-value="displayValue"
      :display-badge="displayBadge"
      :empty-text="emptyText"
      placeholder="Buscar especialidade"
      :sections="sections"
      :search="search"
      :disabled="disabled || !companyId"
      :clearable="clearable"
      :no-results-text="
        search.trim()
          ? `Nenhuma especialidade encontrada para “${search.trim()}”.`
          : 'Nenhuma especialidade disponível.'
      "
      :test-id="testId"
      @update:search="search = $event"
      @select="onSelect"
      @clear="emitSelection(null)"
      @open="onOpen"
    />
    <div
      v-else
      class="rounded-md border border-primary20 p-3 text-left"
      :data-testid="`${testId}-other-panel`"
    >
      <p class="text-xs font-bold text-gray-600">Nova especialidade da empresa</p>
      <input
        v-model="otherName"
        class="mt-1 h-[40px] w-full rounded border border-gray-300 px-3 text-sm"
        :class="{ 'border-solid': isMobileLayout }"
        placeholder="Nome da especialidade"
        maxlength="120"
        :data-testid="`${testId}-other-input`"
      />
      <p
        v-if="otherMatch"
        class="mt-2 text-sm text-gray-700"
        :data-testid="`${testId}-other-match`"
      >
        {{ otherMatchText }}
      </p>
      <p v-else-if="otherName.trim()" class="mt-2 text-sm text-green-700">
        ✓ Nenhuma especialidade parecida. Ao confirmar, “{{ otherName.trim() }}” vira especialidade
        da empresa.
      </p>
      <div class="mt-3 flex flex-wrap gap-2">
        <button
          v-if="otherMatch"
          type="button"
          class="rounded bg-primary30 px-3 py-1.5 text-sm font-semibold text-white"
          :data-testid="`${testId}-use-existing-button`"
          @click="useMatch"
        >
          Usar “{{ otherMatch.option.name }}”{{
            otherMatch.kind === 'official' ? ' (oficial)' : ''
          }}
        </button>
        <button
          v-else
          type="button"
          class="rounded bg-primary30 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
          :disabled="!otherName.trim()"
          :data-testid="`${testId}-create-button`"
          @click="createNew"
        >
          Criar e usar
        </button>
        <button
          type="button"
          class="px-3 py-1.5 text-sm text-primary30"
          :data-testid="`${testId}-back-button`"
          @click="leaveOther"
        >
          Voltar à busca
        </button>
      </div>
    </div>
    <p v-if="loadError" class="mt-1 text-xs text-red-600" :data-testid="`${testId}-load-error`">
      {{ loadError }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import CatalogCombobox from './CatalogCombobox.vue'
import type { CatalogOption, CatalogSection } from './catalogTypes'
import { useSpecialtyStore } from '@/pinia-store/useSpecialtyStore'
import { useIsMobile } from '@/hooks/useIsMobile'
import {
  findSpecialtyMatch,
  matchesCatalogSearch,
  type SpecialtySelection
} from '@/utils/sectorSpecialty'

const props = withDefaults(
  defineProps<{
    modelValue: SpecialtySelection | null
    companyId: number | null
    officialOnly?: boolean
    emptyText?: string
    disabled?: boolean
    clearable?: boolean
    testId?: string
  }>(),
  {
    officialOnly: false,
    emptyText: '— sem especialidade',
    disabled: false,
    clearable: true,
    testId: 'specialty-combobox'
  }
)

const emit = defineEmits<{ 'update:modelValue': [value: SpecialtySelection | null] }>()

const emitSelection = (value: SpecialtySelection | null): void => emit('update:modelValue', value)

const specialtyStore = useSpecialtyStore()
// Celular (QA-01): o reset do Vuetify zera `border-style` em <input>; o desktop fica como está.
const { isMobileLayout } = useIsMobile()
const search = ref('')
const isOtherMode = ref(false)
const otherName = ref('')

watch(
  () => props.companyId,
  (companyId) => {
    if (companyId) void specialtyStore.fetchOptions(companyId)
  },
  { immediate: true }
)

const options = computed(() => specialtyStore.optionsOf(props.companyId))
const loadError = computed(() =>
  props.companyId ? (specialtyStore.loadErrorByCompany[props.companyId] ?? null) : null
)
const displayValue = computed(() => props.modelValue?.name ?? '')
const displayBadge = computed(() => {
  if (!props.modelValue) return null
  if (props.modelValue.kind === 'official') return 'oficial'
  return props.modelValue.kind === 'company' ? 'da empresa' : 'nova'
})

const sections = computed<CatalogSection[]>(() => {
  const result: CatalogSection[] = []
  if (props.modelValue && props.clearable) {
    result.push({
      key: 'clear',
      title: null,
      options: [
        {
          key: 'clear',
          label: '— Sem especialidade',
          kind: 'clear',
          testId: `${props.testId}-clear-option`
        }
      ]
    })
  }
  const official = (options.value?.official ?? []).filter((o) =>
    matchesCatalogSearch(o.name, search.value)
  )
  result.push({
    key: 'official',
    title: 'Oficiais (CFM)',
    options: official.map((o) => ({
      key: `o:${o.id}`,
      label: o.name,
      kind: 'option',
      testId: `${props.testId}-option-o-${o.id}`
    }))
  })
  if (props.officialOnly) return result
  const company = (options.value?.company ?? []).filter((o) =>
    matchesCatalogSearch(o.name, search.value)
  )
  result.push({
    key: 'company',
    title: 'Da sua empresa',
    options: company.map((o) => ({
      key: `c:${o.id}`,
      label: o.name,
      kind: 'option',
      testId: `${props.testId}-option-c-${o.id}`
    }))
  })
  const typed = search.value.trim()
  result.push({
    key: 'other',
    title: null,
    options: [
      {
        key: 'other',
        label: typed ? `✎ Outra… usar “${typed}” como nova` : '✎ Outra… (digitar)',
        kind: 'action',
        testId: `${props.testId}-other-option`
      }
    ]
  })
  return result
})

const otherMatch = computed(() => findSpecialtyMatch(otherName.value, options.value))
const otherMatchText = computed(() => {
  if (!otherMatch.value) return ''
  const name = otherMatch.value.option.name
  return otherMatch.value.kind === 'official'
    ? `“${name}” já existe na lista oficial. Usar essa?`
    : `Já existe “${name}” na sua empresa. Usar essa?`
})

async function onOpen(): Promise<void> {
  if (props.companyId) await specialtyStore.fetchOptions(props.companyId)
}

function onSelect(option: CatalogOption): void {
  if (option.key === 'clear') {
    emitSelection(null)
    return
  }
  if (option.key === 'other') {
    otherName.value = search.value.trim()
    isOtherMode.value = true
    return
  }
  const [kind, rawId] = option.key.split(':')
  const id = Number(rawId)
  const list = kind === 'o' ? options.value?.official : options.value?.company
  const found = list?.find((o) => o.id === id)
  if (found)
    emitSelection({
      kind: kind === 'o' ? 'official' : 'company',
      id: found.id,
      name: found.name
    })
}

function useMatch(): void {
  if (!otherMatch.value) return
  const { kind, option } = otherMatch.value
  emitSelection({ kind, id: option.id, name: option.name })
  leaveOther()
}

function createNew(): void {
  const name = otherName.value.trim()
  if (!name) return
  emitSelection({ kind: 'new', name })
  leaveOther()
}

function leaveOther(): void {
  isOtherMode.value = false
  otherName.value = ''
  search.value = ''
}
</script>
